import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import './PWAInstallPrompt.css';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if running in standalone mode (already installed)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after 5 seconds if not dismissed before
      setTimeout(() => {
        const dismissed = localStorage.getItem('pwa-prompt-dismissed');
        if (!dismissed && !standalone) {
          setShowPrompt(true);
        }
      }, 5000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS prompt after 5 seconds
    if (iOS && !standalone) {
      setTimeout(() => {
        const dismissed = localStorage.getItem('pwa-prompt-dismissed');
        if (!dismissed) {
          setShowPrompt(true);
        }
      }, 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt && !isIOS) {
      return;
    }

    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted' && process.env.NODE_ENV === 'development') {
        console.log('PWA installed');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
    
    // Clear dismissal after 7 days
    setTimeout(() => {
      localStorage.removeItem('pwa-prompt-dismissed');
    }, 7 * 24 * 60 * 60 * 1000);
  };

  if (!showPrompt || isStandalone) {
    return null;
  }

  return (
    <div className="pwa-install-prompt">
      <button className="pwa-close" onClick={handleDismiss}>
        <X size={20} />
      </button>
      
      <div className="pwa-icon">
        <Download size={32} />
      </div>
      
      <div className="pwa-content">
        <h3 className="pwa-title">Install Soul Aligned</h3>
        <p className="pwa-description">
          {isIOS 
            ? 'Tap the share button and select &quot;Add to Home Screen&quot;'
            : 'Install our app for quick access and offline use'
          }
        </p>
        
        {!isIOS && (
          <button className="btn-primary" onClick={handleInstallClick}>
            Install App
          </button>
        )}
        
        {isIOS && (
          <div className="ios-instructions">
            <p>1. Tap the Share button <span style={{ fontSize: '1.2rem' }}>⎘</span></p>
            <p>2. Scroll and tap &quot;Add to Home Screen&quot;</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
